// src/pages/.../AdsRegist1.jsx (AdsRegistStep1)

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

import StepAnimator from "./detailComponents/StepAnimator";
import kakao from "../../../assets/image/login/Kakaotalk.svg";
import google from "../../../assets/image/login/google_icon.svg";
import apple from "../../../assets/image/login/apple_icon.svg";
import test from "../../../assets/image/login/test_icon.svg";
import { postToApp } from "../../../bridges/appBridge";
import SectionTitle from "../../../assets/components/TitleGroup";
import Tooltip from "../../../assets/components/Tooltip";

const API = process.env.REACT_APP_FASTAPI_ADS_URL;

const AdsRegistStep1 = ({
    isOK,
    resetAuthOK,
    onNext,
    session,
    isAuthed,
    loading,
    error,
    initialMode,
    allowAutoNext = true,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const platform = localStorage.getItem("platform");
    const jumpedRef = useRef(false);

    const [hasSignInStarted, setHasSignInStarted] = useState(false);

    // 1) 회원가입 / 로그인 모드 분기
    //    - 기본값: signup
    //    - /ads/register?step=1&auth=login 으로 들어오면 login 모드로 시작
    const [mode, setMode] = useState(() => {
        if (initialMode === "login" || initialMode === "signup") return initialMode;

        try {
            const search = new URLSearchParams(location.search);
            const auth = search.get("auth");
            if (auth === "login") return "login";
        } catch { }

        return "signup";
    });

    // 부모에서 initialMode가 바뀌면 모드 재설정
    useEffect(() => {
        if (initialMode === "login" || initialMode === "signup") {
            setMode(initialMode);
        }
    }, [initialMode]);

    const isLoginMode = mode === "login";

    const modeIndex = isLoginMode ? 1 : 0;

    // 2) 최근 로그인 provider (로그인 모드에서만 사용)
    const installationId = localStorage.getItem("install_id");
    const [recentProvider, setRecentProvider] = useState(null);

    // App → Web 메시지 수신 (기존 그대로)
    useEffect(() => {
        const onMsg = (e) => {
            let data = e.data;
            try {
                if (typeof data === "string") data = JSON.parse(data);
            } catch {
                // 문자열이 JSON이 아닐 수도 있음 → 무시
            }
            if (!data || typeof data !== "object") return;
        };

        window.addEventListener("message", onMsg);
        return () => window.removeEventListener("message", onMsg);
    }, []);

    // SNS 로그인 성공 시 다음 스텝으로 이동 (기존 로직 유지)
    useEffect(() => {
        // 자동이동 자격:
        // 1) Step1을 처음 통과하는 흐름이면 allowAutoNext로 허용
        // 2) 재방문(allowAutoNext가 false)이라도, 이번 방문에서 로그인 버튼을 눌렀다면(hasSignInStarted) 허용
        const eligible = allowAutoNext || hasSignInStarted;

        if (!eligible) return;

        // isOK가 true가 되었을 때 딱 한 번만 이동
        if (!jumpedRef.current && isOK) {
            jumpedRef.current = true;
            onNext?.();
        }
    }, [isOK, allowAutoNext, hasSignInStarted, onNext]);


    // 로그인 모드일 때만 최근 로그인 provider 조회
    useEffect(() => {
        if (!isLoginMode) return;
        if (!installationId) return;

        let cancelled = false;

        const fetchRecentLoginProvider = async () => {
            try {
                const { data } = await axios.get(`${API}/ads/recent/login`, {
                    params: { install_id: installationId },
                });

                if (!cancelled) {
                    setRecentProvider(data?.login_provider ?? null);
                    console.log("최근 로그인 provider:", data?.login_provider);
                }
            } catch (e) {
                if (!cancelled) {
                    console.error("최근 로그인 provider 조회 실패:", e);
                    setRecentProvider(null);
                }
            }
        };

        fetchRecentLoginProvider();

        return () => {
            cancelled = true;
        };
    }, [isLoginMode, installationId]);

    const startSignin = (provider) => {
        // 새 로그인 시도 시작 전에 이전 성공 상태 초기화
        if (typeof resetAuthOK === "function") {
            resetAuthOK();
        }

        // 이번 Step1 방문에서 “로그인 시도”가 있었다는 표시
        setHasSignInStarted(true);
        // 새 로그인 시도이므로 이전 jump 기록 초기화
        jumpedRef.current = false;

        const message = { type: "START_SIGNIN", payload: { provider } };

        // 디버그 이벤트(화면에 표시용)
        window.dispatchEvent(
            new MessageEvent("message", {
                data: JSON.stringify({ type: "SEND_DEBUG", payload: message }),
            })
        );

        // 실제 앱으로 전송
        postToApp(message);
    };

    const handleGoogleLogin = () => startSignin("google");
    const handleKakaoLogin = () => startSignin("kakao");
    const handleAppleLogin = () => startSignin("apple");



    return (
        <div className="h-full relative overflow-hidden">
            <StepAnimator stepKey={modeIndex}>
                {/* key로 mode를 주면 내부 캐싱에도 안전 */}
                <div key={mode} className="p-[16px]">
                    {/* 상단 타이틀 - 모드에 따라 문구만 변경 */}
                    <SectionTitle
                        title={isLoginMode ? "위즈마켓 로그인하기" : "위즈마켓 회원가입하기"}
                        subTitle={"서비스 이용에 사용할 소셜 계정을 선택해 주세요."}
                    />

                    {/* SNS 로그인 버튼들 */}
                    <div className="fixed left-[16px] right-[16px] bottom-[40px]">
                        <div className="space-y-[16px] ">
                            {/* 카카오 버튼 + (로그인 모드일 때만) 최근 로그인 툴팁 */}
                            <div className="relative">
                                {isLoginMode && recentProvider === "kakao" && (
                                    <Tooltip text="최근 로그인" selectable={false} />
                                )}

                                <button
                                    type="button"
                                    onClick={handleKakaoLogin}
                                    className="w-full rounded-[8px] bg-[#FFE812] py-[12px]"
                                >
                                    <div className="flex items-center justify-center gap-[8px] ">
                                        <img src={kakao} alt="Kakao" className="w-[24px] h-[24px]" />
                                        <span className="text-b1 text-text-default break-keep">
                                            {`카카오톡 계정으로 ${isLoginMode ? "로그인" : "회원가입"}`}
                                        </span>
                                    </div>
                                </button>
                            </div>

                            {/* 구글 버튼 + 최근 로그인 툴팁 */}
                            <div className="relative">
                                {isLoginMode && recentProvider === "google" && (
                                    <Tooltip text="최근 로그인" selectable={false} />
                                )}

                                <button
                                    type="button"
                                    onClick={handleGoogleLogin}
                                    className="w-full h-[48px] border border-border-default rounded-[8px] py-[12px]"
                                >
                                    <div className="flex items-center justify-center gap-[8px]">
                                        <img src={google} alt="Google" className="w-[24px] h-[24px]" />
                                        <span className="text-b1 text-text-default break-keep">
                                            {`구글 계정으로 ${isLoginMode ? "로그인" : "회원가입"}`}
                                        </span>
                                    </div>
                                </button>
                            </div>

                            {/* 애플 버튼 + 최근 로그인 툴팁 (iOS 한정) */}
                            {platform === "ios" && (
                                <div className="relative">
                                    {isLoginMode && recentProvider === "apple" && (
                                        <Tooltip text="최근 로그인" selectable={false} />
                                    )}

                                    <button
                                        type="button"
                                        onClick={handleAppleLogin}
                                        className="w-full h-[48px] border border-border-default rounded-[8px] py-[12px]"
                                    >
                                        <div className="flex items-center justify-center gap-[8px]">
                                            <img src={apple} alt="Apple" className="w-[24px] h-[24px]" />
                                            <span className="text-b1 text-text-default break-keep">
                                                {`애플 계정으로 ${isLoginMode ? "로그인" : "회원가입"}`}
                                            </span>
                                        </div>
                                    </button>
                                </div>
                            )}

                        {/* 위즈마켓 자체 계정 로그인 버튼 (그대로 유지) */}
                            <button
                                type="button"
                                onClick={() => navigate(`/ads/login/MA010120220808570604`)}
                                className="w-full h-[48px] border border-border-default rounded-[8px] py-[12px]"
                            >
                                <div className="flex items-center justify-center gap-[8px]">
                                    <img src={test} alt="wizmarket" className="w-[24px] h-[24px]" />
                                    <span className="text-b1 text-text-default break-keep">
                                        위즈마켓 계정으로 로그인
                                    </span>
                                </div>
                            </button>
                        </div>

                        {/* 가장 아래 토글 버튼
                    - 1번 분기(회원가입)일 땐 "로그인" 글자
                    - 2번 분기(로그인)일 땐 "회원가입" 글자
                */}
                        <div className="pt-[8px] gap-[8px] flex justify-center items-center text-b1 text-text-tertiary">
                            {/* 왼쪽 버튼: 모드 토글 */}
                            <button
                                type="button"
                                className="h-[36px]"
                                onClick={() => setMode(isLoginMode ? "signup" : "login")}
                            >
                                {isLoginMode ? "회원가입" : "로그인"}
                            </button>

                            <span className="">・</span>

                            {/* 오른쪽 버튼: 고객센터 (기존 로직 유지, 추후 링크만 채우면 됨) */}
                            <button
                                className="h-[36px]"
                                type="button"
                                onClick={() =>
                                    navigate("/ads/support", {
                                        state: { origin: isLoginMode ? "login" : "signup" },
                                    })
                                }
                            >
                                고객지원
                            </button>
                        </div>
                    </div>
                </div>
            </StepAnimator>
        </div>
    );
};

export default AdsRegistStep1;
